import { Slot } from '@radix-ui/react-slot';
import { LoadingCircleIconV2 } from '@repo/icons';
import { cn } from '@repo/utils/cn';
import { type VariantProps, cva } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';

const buttonVariants = cva(
  'vt-font-weight-bold rounded-[0.25rem] group items-center inline-flex z-1 whitespace-nowrap focus-visible:outline-none ring-inherit disabled:cursor-not-allowed duration-200 transition-all cursor-pointer',
  {
    variants: {
      variant: {
        primary:
          'vt-atoms-btn-primary-text disabled:vt-atoms-btn-primary-text-disabled disabled:vt-atoms-btn-primary-bg-disabled active:vt-atoms-btn-primary-bg-active vt-atoms-btn-primary-bg hover:vt-atoms-btn-primary-text-hover hover:vt-atoms-btn-primary-bg-hover hover:ring-2 hover:ring-[var(--atoms-btn-primary-bg-hover)] hover:ring-offset-1 hover:ring-offset-[var(--atoms-btn-primary-text)] vt-font-highlight-font-family vt-text-size-medium',
        secondary:
          'vt-atoms-btn-secondary-bg hover:vt-atoms-btn-secondary-bg-hover disabled:vt-atoms-btn-secondary-bg-disabled active:vt-atoms-btn-secondary-bg-active border vt-atoms-btn-secondary-stroke hover:vt-atoms-btn-secondary-stroke-hover disabled:vt-atoms-btn-secondary-stroke-disabled active:vt-atoms-btn-secondary-stroke-active vt-atoms-btn-secondary-text hover:vt-atoms-btn-secondary-text-hover disabled:vt-atoms-btn-secondary-text-disabled active:vt-atoms-btn-secondary-text-active vt-font-highlight-font-family vt-text-size-medium sm:vt-text-size-medium-sm',
        tertiary:
          'vt-atoms-btn-tertiary-bg hover:vt-atoms-btn-tertiary-bg-hover disabled:vt-atoms-btn-tertiary-bg-disabled active:vt-atoms-btn-tertiary-bg-active border vt-atoms-btn-tertiary-stroke hover:vt-atoms-btn-tertiary-stroke-hover disabled:vt-atoms-btn-tertiary-stroke-disabled active:vt-atoms-btn-tertiary-stroke-active vt-atoms-btn-tertiary-text hover:vt-atoms-btn-tertiary-text-hover disabled:vt-atoms-btn-tertiary-text-disabled active:vt-atoms-btn-tertiary-text-active vt-font-highlight-font-family vt-text-size-medium sm:vt-text-size-medium-sm',
        inverted:
          'vt-atoms-btn-inverted-bg hover:vt-atoms-btn-inverted-bg-hover disabled:vt-atoms-btn-inverted-bg-disabled active:vt-atoms-btn-inverted-bg-active border vt-atoms-btn-inverted-stroke hover:vt-atoms-btn-inverted-stroke-hover disabled:vt-atoms-btn-inverted-stroke-disabled active:vt-atoms-btn-inverted-stroke-active vt-atoms-btn-inverted-text hover:vt-atoms-btn-inverted-text-hover disabled:vt-atoms-btn-inverted-text-disabled active:vt-atoms-btn-inverted-text-active vt-font-highlight-font-family vt-text-size-medium sm:vt-text-size-medium-sm',
        blank:
          'rounded-none vt-atoms-btn-blank-bg hover:vt-atoms-btn-blank-bg-hover disabled:vt-atoms-btn-blank-bg-disabled active:vt-atoms-btn-blank-bg-active border vt-atoms-btn-blank-stroke hover:vt-atoms-btn-blank-stroke-hover disabled:vt-atoms-btn-blank-stroke-disabled active:vt-atoms-btn-blank-stroke-active vt-atoms-btn-blank-text hover:vt-atoms-btn-blank-text-hover disabled:vt-atoms-btn-blank-text-disabled active:vt-atoms-btn-blank-text-active vt-text-size-medium sm:vt-text-size-medium-sm',
        grey: 'bg-transparent rounded-none text-[var(--atoms-btn-grey-text)] hover:text-[var(--atoms-btn-grey-text-hover)] disabled:text-[var(--atoms-btn-grey-text-disabled)] active:text-[var(--atoms-btn-grey-text-active)] vt-text-size-medium sm:vt-text-size-medium-sm',
      },
      size: {
        default: 'px-4 py-2 h-10',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
      },
      loading: {
        true: 'pointer-events-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  childrenWrapperClassName?: string;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ childrenWrapperClassName, className, asChild, variant = 'primary', size, children, loading, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={cn(buttonVariants({ variant, size, loading }), className)}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, loading }), className)}
        aria-disabled={loading}
        {...props}
      >
        <div className="relative z-[2] w-full">
          <div
            className={cn(
              { invisible: loading },
              'flex w-full items-center justify-center space-x-1 text-center rtl:space-x-reverse',
              childrenWrapperClassName
            )}
          >
            {children}
          </div>
          {loading && (
            <span className="pointer-events-none absolute left-0 top-0 flex h-full w-full items-center justify-center">
              <LoadingCircleIconV2 className="size-7 animate-spin duration-700" />
            </span>
          )}
        </div>
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
